import { z } from 'zod';
import { TimestampSchema } from '../common.js';
/**
 * One turn in a session's voice / text transcript — see docs/data-model.md §4.12.
 *
 * PK = TENANT#<tenantId>
 * SK = SESSION#<sessionId>#TURN#<seq padded to 6 digits>
 *
 * `seq` is a monotonic integer starting at 1. Zero-padding to 6 digits keeps
 * lex-order aligned with numeric-order for DDB range queries.
 */
export const TranscriptRoleSchema = z.enum(['user', 'assistant']);
/** Upper bound on a transcript turn body. Truncated server-side. */
export const TRANSCRIPT_TEXT_MAX_CHARS = 8192;
/**
 * Voice provider that produced this turn. Same enum-shape lives on the
 * voice-rates SSM table (`packages/shared-types/src/voiceRates.ts`) so the
 * pricing key `${voiceProvider}/${model}` can be assembled deterministically
 * at ingest time. Phase-2 ingest fills these fields from the agent-runtime
 * report; today every entry is optional so existing rows still parse.
 */
export const KnownTurnVoiceProviderSchema = z.enum(['openai-realtime', 'vapi', 'openai-text']);
export const TurnVoiceProviderSchema = z.union([
    KnownTurnVoiceProviderSchema,
    z.string().min(1).max(80),
]);
/**
 * Per-turn token usage as reported by the model provider. Values are integer
 * counts. The `*Text` and `*Audio` splits sum to the parent (`inputTokens`,
 * `outputTokens`) — phase-2 ingest does not enforce that arithmetic, but
 * downstream cost calc relies on the per-modality breakdown so callers
 * should always populate it.
 */
export const TurnUsageSchema = z.object({
    inputTokens: z.number().int().nonnegative(),
    inputTokensCached: z.number().int().nonnegative(),
    inputTokensCachedText: z.number().int().nonnegative().optional(),
    inputTokensCachedAudio: z.number().int().nonnegative().optional(),
    inputTokensText: z.number().int().nonnegative(),
    inputTokensAudio: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    outputTokensText: z.number().int().nonnegative(),
    outputTokensAudio: z.number().int().nonnegative(),
    totalTokens: z.number().int().nonnegative(),
});
/**
 * One tool/function call performed during a turn. `arguments` mirrors the
 * JSON sent to the tool; `result` is whatever the tool returned (free-form so
 * we don't have to cut a release for every new tool). Capped per-turn in the
 * parent schema to bound the row size.
 */
export const TurnToolCallSchema = z.object({
    name: z.string().min(1).max(120),
    arguments: z.record(z.unknown()),
    result: z.unknown().optional(),
    durationMs: z.number().int().nonnegative(),
});
export const TranscriptTurnSchema = z.object({
    turnId: z.string().uuid(),
    sessionId: z.string().uuid(),
    tenantId: z.string().uuid(),
    seq: z.number().int().positive(),
    role: TranscriptRoleSchema,
    text: z.string().max(TRANSCRIPT_TEXT_MAX_CHARS),
    timestamp: TimestampSchema,
    // ---- Reporting fields (all optional; phase-2 ingest populates) ----
    /** Audio duration covered by this turn, in milliseconds. Transcript-only — no raw bytes. */
    audioMs: z.number().int().nonnegative().optional(),
    /** End-to-end latency in milliseconds (e.g. user audio end → assistant first audio). */
    latencyMs: z.number().int().nonnegative().optional(),
    /** Model that produced this turn (e.g. `gpt-realtime-2.1`). */
    modelUsed: z.string().min(1).max(120).optional(),
    /** Voice id used to render the turn (e.g. `alloy`). */
    voiceUsed: z.string().min(1).max(80).optional(),
    /** Voice provider — combined with `modelUsed` to look up the rate row. */
    voiceProvider: TurnVoiceProviderSchema.optional(),
    /** Token usage as reported by the provider. */
    usage: TurnUsageSchema.optional(),
    /**
     * Cost in USD computed server-side at ingest using the rate-table snapshot
     * in effect at the time the turn was written. Persisted so historical
     * sessions don't drift when rates change later.
     */
    costUsd: z.number().nonnegative().optional(),
    /** True when the user interrupted the assistant mid-turn. */
    interrupted: z.boolean().optional(),
    /** Tool calls performed during this turn. Capped to bound DDB row size. */
    toolCalls: z.array(TurnToolCallSchema).max(20).optional(),
    /** Stable error code if the turn failed (e.g. `model_overloaded`). */
    errorCode: z.string().min(1).max(80).optional(),
});
//# sourceMappingURL=transcriptTurn.js.map